import { Button, Card, CardContent, CardDescription, CardHeader, Input } from "@beweegbaas/ui";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { z } from "zod/v4";
import { Page } from "#/components/page";
import { FaqAccordion } from "#/modules/help/components/faq-accordion";
import { startRondleiding } from "#/modules/les.planning/rondleiding/rondleiding";
import { faqCategories, searchFaq } from "#/modules/help/faq";
import { TicketDialog } from "#/modules/tickets/components/ticket-dialog";
import { useDebouncedSearchCommit } from "#/modules/util.helpers/use-debounced-search-commit";
import { m } from "#/paraglide/messages";

const helpSearchSchema = z.object({ q: z.string().default("") });
const HELP_SEARCH_DEFAULTS = { q: "" };

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/help/")({
  validateSearch: helpSearchSchema,
  search: { middlewares: [stripSearchParams(HELP_SEARCH_DEFAULTS)] },
  component: HelpPage,
});

/** Ticket entry point for questions and suggestions, shown above the FAQ. */
function NotFoundCard() {
  const params = Route.useParams();
  return (
    <Card size="sm">
      <CardHeader>
        <CardDescription>{m["help.not_found_description"]()}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={startRondleiding} data-rondleiding-starten>
          <IconMaterialSymbolsTourOutline />
          {m["rondleiding.bekijken"]()}
        </Button>
        <TicketDialog
          trigger={
            <Button>
              <IconMaterialSymbolsAdd />
              {m["tickets.list.new_ticket"]()}
            </Button>
          }
        />
        <Button
          variant="outline"
          nativeButton={false}
          render={<Link to="/$locale/{-$schoolyear}/$school/tickets" params={params} />}
        >
          {m["tickets.list.title"]()}
        </Button>
      </CardContent>
    </Card>
  );
}

function HelpPage() {
  // URL-persisted search (survives refresh/back); input tracks locally and commits debounced
  // with replace, matching the overview pages' search convention.
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [query, setQuery] = useDebouncedSearchCommit(
    q,
    (value) =>
      void navigate({
        search: (prev) => ({ ...prev, q: value }),
        replace: true,
        resetScroll: false,
      }),
    300,
  );
  const results = q.trim() ? searchFaq(q, 20) : null;

  return (
    <Page size="xs" center={false}>
      <Page.Header>
        <Page.Title>{m["help.title"]()}</Page.Title>
      </Page.Header>
      <Page.Container>
        <Page.Body className="flex flex-col gap-8">
          <NotFoundCard />

          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={m["help.search_placeholder"]()}
            aria-label={m["help.search_placeholder"]()}
          />

          {results ? (
            results.length > 0 ? (
              <FaqAccordion items={results} source="help-search" />
            ) : (
              <p className="text-muted-foreground text-sm">{m["help.no_results"]()}</p>
            )
          ) : (
            faqCategories.map((category) => (
              <section key={category.name} className="flex flex-col gap-2">
                <h2 className="text-muted-foreground text-sm font-semibold tracking-wider uppercase">
                  {category.name}
                </h2>
                <FaqAccordion items={category.items} source="help-page" />
              </section>
            ))
          )}
        </Page.Body>
      </Page.Container>
    </Page>
  );
}
